// FNP-1248 — the write half.
//
// ⚠️ NOTHING IN THIS FILE SELECTS A CREDENTIAL BACK, BECAUSE NOTHING CAN.
// `ConnectProviderCredentialInput` is a GraphQL INPUT type and an input type
// cannot be a field type — so there is no selection set, on any mutation here,
// that could return a token or a set of keys. The guarantee is structural, not
// a convention these strings happen to honour.
//
// The selection sets mirror services/queries.ts and omit `secretRef.secretArn`
// for the same reason documented there. `externalId` IS selected on connect and
// rotate, and that is load-bearing: a first-time ASSUMED_ROLE connect returns
// the newly-minted external id, and it is the single most important value in
// that response — see types.ts `isAwaitingTrustPolicy`.

const CAPABILITY_FIELDS = `
    supportedRecordTypes
    canWriteRecords
    canCreateZones
    canDeleteZones
    canSetNameservers
`;

// FNP-1377 — THE TENANT'S OWN DECLARATIONS, WHICH NOTHING USED TO ASK FOR.
//
// ⚠️ `writable` AND `certificateCapabilities` ARE NOT PART OF `capabilities`,
// and that is why they were missed. `capabilities` is what the credential was
// OBSERVED to do at validation time; these two are what the tenant DECLARED it
// may do. Selecting the first and forgetting the second left
// `isWritableDeclared` reading `undefined` on every connection the client had
// ever seen, so the detail page rendered "Declared to write: READ-ONLY" for a
// connection whose stored row said `writable: true` — directly above three rows
// saying YES.
//
// ⚠️ THIS IS THE WRITE-PATH-WITHOUT-READ-PATH SHAPE, for the third time
// (FNP-1360 shipped a write-only field, FNP-1369 was its read half). A field
// wired into the write path has to be opened again on the read path, and
// neither the compiler nor a test noticed for either of them. The test at
// `tests/selectionSet.test.ts` is what makes this one different.
//
// Both are non-null on the deployed type — verified against the assembled
// schema by introspection, not the source — so a stale schema would have
// nulled the whole list rather than one badge.
const DECLARATION_FIELDS = `
    dnsCapabilities {
        canWriteRecords
        canCreateZones
        canDeleteZones
    }
    certificateCapabilities {
        canRequest
        canDelete
        supportedRegions
    }
`;

const PROVIDER_CONNECTION_FIELDS = `
    id
    providerId
    displayName
    credentialType
    status
    statusDetail
    lastValidatedAt
    capabilities {${CAPABILITY_FIELDS}}
    ${DECLARATION_FIELDS}
    secretRef {
        externalId
    }
    createdByUsername
    created
    updated
`;

// ⚠️ A REJECTED CREDENTIAL IS A SUCCESSFUL MUTATION HERE.
//
// `connectProvider` validates through the adapter, then STORES the connection
// whatever the adapter said — INVALID/UNREACHABLE with the provider's own
// message, never a silently healthy record and never a refusal to store. The
// GraphQL call RESOLVES in that case; it does not throw.
//
// That is exactly what makes the external-id flow possible: the connection has
// to exist for an external id to be derived from its id, so a first ASSUMED_ROLE
// connect is EXPECTED to come back not-ACTIVE with a populated externalId. The
// mutation hook does not treat a non-ACTIVE status as an error, and the drawer
// does not close on failure — it swaps to showing the tenant their external id.
export const connectProvider = `
   mutation connectProvider($input: ConnectProviderInput!) {
       connectProvider(input: $input) {${PROVIDER_CONNECTION_FIELDS}}
   }
`;

// ⚠️ A MUTATION THAT MUTATES NOTHING, AND ITS EXTERNAL ID IS A THROWAWAY.
//
// It is a Mutation rather than a Query because it takes credential material as
// an argument, and queries are what clients cache, log and retry — a query
// carrying a secret in its arguments is one some client puts in a URL or an APQ
// cache key.
//
// ⚠️ IT CANNOT PRE-VALIDATE A REAL ROUTE 53 ASSUMPTION. The external id it
// mints is never stored and never returned, so the tenant cannot put it in a
// trust policy, so the assume-role cannot succeed. For ASSUMED_ROLE this test
// can only ever confirm that an ARN is well-formed. The module uses it that way
// and says so on screen — see hooks/useTestProviderCredential.
export const testProviderCredential = `
   mutation testProviderCredential($input: TestProviderCredentialInput!) {
       testProviderCredential(input: $input) {
           ok
           status
           statusDetail
           capabilities {${CAPABILITY_FIELDS}}
       }
   }
`;

// ⚠️ THE RE-VALIDATE PATH — how a tenant fixes a connection after editing their
// trust policy.
//
// The returned connection keeps the SAME id, displayName, created AND external
// id; only the credential, status, capabilities and timestamps move. Keeping
// the external id is what makes the loop terminate: the tenant pasted THAT id
// into their trust policy, and a rotation that minted a new one would invalidate
// the thing they just did.
//
// Rotating to a credential the provider rejects records the failure and KEEPS
// the connection. It is never deleted for being broken.
export const rotateProviderCredential = `
   mutation rotateProviderCredential($input: RotateProviderCredentialInput!) {
       rotateProviderCredential(input: $input) {${PROVIDER_CONNECTION_FIELDS}}
   }
`;

// ⚠️ CALL `previewProviderDisconnect` FIRST AND SHOW THE CALLER WHAT IT SAYS.
//
// This mutation does not ask for confirmation, because a server cannot verify
// that a human saw anything — the preview exists so the interface can. This
// module enforces that ordering in `useProviderDisconnect`: the confirm button
// is not armed until a preview has resolved.
export const disconnectProvider = `
   mutation disconnectProvider($input: DisconnectProviderInput!) {
       disconnectProvider(input: $input) {
           connectionId
           secretDeleted
           secretName
           connectionDeleted
       }
   }
`;

// FNP-1304 — CHANGE THE LABEL, TOUCH NOTHING ELSE.
//
// ⚠️ THE RETURNED CONNECTION IS THE SAME ONE, not a re-validated one. Only
// `displayName` and `updated` move; status, statusDetail, lastValidatedAt,
// capabilities and the external id all come back exactly as they were. That is
// the whole point of a rename existing separately from `rotateProviderCredential`
// — a tenant fixing a typo must not have to touch their AWS trust policy, and
// renaming a broken connection must not make it look repaired.
//
// The full field set is selected deliberately: the response is what refreshes
// the cache, so it has to carry everything the detail page and the list render.
export const renameProviderConnection = `
   mutation renameProviderConnection($input: RenameProviderConnectionInput!) {
       renameProviderConnection(input: $input) {${PROVIDER_CONNECTION_FIELDS}}
   }
`;

// FNP-1400 — CORRECT WHAT THE TENANT DECLARED, WITHOUT TOUCHING THE CREDENTIAL.
//
// ⚠️ THE INPUT CARRIES NO CREDENTIAL, AND THAT IS THE WHOLE POINT. A
// declaration is the tenant's statement about the IAM grants they made in
// their own account; correcting it means "the credential is unchanged and I
// described it wrongly". Routing that through `rotateProviderCredential` would
// demand a role ARN back — and the ARN is readable from no output type, so the
// tenant would retype it from memory. That is the FNP-1265 trap: a blank
// required field where a typo silently repoints the connection at a different
// role.
//
// ⚠️ NOT `revalidateProviderConnection` EITHER. That takes an id and nothing
// else, deliberately, so a re-check can never revoke a capability by omission.
// Correcting and re-checking are different intents.
//
// Both blocks are optional and at least one is required; an omitted block keeps
// the current declaration, an omitted field keeps that value, an explicit false
// revokes.
export const declareProviderCapabilities = `
   mutation declareProviderCapabilities($input: DeclareProviderCapabilitiesInput!) {
       declareProviderCapabilities(input: $input) {${PROVIDER_CONNECTION_FIELDS}}
   }
`;

// FNP-1269 — RE-VALIDATE WITHOUT RE-SUPPLYING A CREDENTIAL.
//
// ⚠️ THE INPUT IS AN ID AND NOTHING ELSE, AND THAT IS THE WHOLE FIX.
//
// `rotateProviderCredential` requires a non-null `credential`, so the only way
// to re-check an ASSUMED_ROLE connection was to make the tenant retype their
// role ARN. But re-validating is not a credential change: the flow is connect →
// Flexi mints an external id → the tenant pastes it into their trust policy →
// the tenant comes back and asks Flexi to look again. Nothing about the
// credential moved. Demanding it back made a typo in the retyped ARN silently
// repoint the connection at a DIFFERENT role, and the UI never prefilled the
// field, so every re-validate was a retype from scratch.
//
// This re-checks the credential ALREADY in Secrets Manager, makes a real
// provider call, and records status/capabilities/lastValidatedAt. It writes no
// secret and mints no new external id.
//
// ⚠️ IT DECLARES NO `writable` AND NO CERTIFICATE CAPABILITY, DELIBERATELY.
// Omitting them is what PRESERVES the connection's existing declarations. A
// caller that helpfully re-sent them would turn a read-only re-check into a
// capability write, and a tenant who did not re-tick a switch would have a
// capability silently revoked.
//
// Same selection set as rotate: the response is what refreshes the cache, so it
// has to carry everything the detail page and the list render.
export const revalidateProviderConnection = `
   mutation revalidateProviderConnection($input: RevalidateProviderConnectionInput!) {
       revalidateProviderConnection(input: $input) {${PROVIDER_CONNECTION_FIELDS}}
   }
`;
